import React, { useEffect, useRef, useState } from 'react';
import { useQuoteStore } from '@/store/quoteStore';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Calculator, Edit3, Trash2, Eye } from 'lucide-react';
import BorderStepperInput from '@/components/border/BorderStepperInput';
import BorderCalculateInputs from '@/components/border/BorderCalculateInputs';

const SURFACE_BLENDS = [
  { 
    group: 'Traditional', 
    blends: ['Woodland***', 'Butterscotch***', 'Sahara***', 'Sand Dune', 'Maya Beach', 'Valencia***', 'Autumn Country***'] 
  },
  { 
    group: 'Classic', 
    blends: ['Crimson Sands', 'Potala Palace', 'Amroth', 'Russo Azure', 'Galapagos', 'Caspian', 'St Moritz'] 
  },
  { 
    group: 'Modern', 
    blends: ['Eclipse', 'Midnight Sapphire', 'Pebble Beach', 'Oceana', 'Empire', 'Antarctica'] 
  },
  { 
    group: 'Contemporary', 
    blends: ['Arctic Blue', 'Howlite', 'Merlin', 'Liberty', 'Aventurine', 'Atlas'] 
  }
];

const Border = () => {
  const { border, setBorder } = useQuoteStore();
  const [showBorder, setShowBorder] = useState(!!border);
  const [mode, setMode] = useState(border?.mode || 'manual'); // 'manual' or 'calculate'
  
  // Calculate mode inputs
  const [length, setLength] = useState(border?.length || '');
  const [width, setWidth] = useState(border?.width || '');
  
  // Border values (editable in both modes)
  const [blendColor, setBlendColor] = useState(border?.blend_color || '');
  const [borderMixes, setBorderMixes] = useState(border?.mixes || 0);
  const [rigidTrim, setRigidTrim] = useState(border?.rigid_trim || 0); // individual lengths (2.5m each)
  const [formableTrim, setFormableTrim] = useState(border?.formable_trim || 0); // individual lengths (2.5m each)
  
  // Store suggested values (calculated from dimensions, don't change unless dimensions change)
  const [suggestedMixes, setSuggestedMixes] = useState(border?.suggested_mixes || 0);
  const [suggestedRigidTrim, setSuggestedRigidTrim] = useState(border?.suggested_rigid_trim || 0);

  // Refs so the "only overwrite if user hasn't manually edited" check inside
  // the calculate effect can read the freshest values without adding them to
  // the dep array (which would cause the effect to re-fire on every keystroke
  // and clobber manual edits).
  const borderMixesRef = useRef(borderMixes);
  const rigidTrimRef = useRef(rigidTrim);
  const borderRef = useRef(border);
  useEffect(() => { borderMixesRef.current = borderMixes; }, [borderMixes]);
  useEffect(() => { rigidTrimRef.current = rigidTrim; }, [rigidTrim]);
  useEffect(() => { borderRef.current = border; }, [border]);

  // Calculate suggested values when length/width change (only in calculate mode)
  useEffect(() => {
    if (mode === 'calculate' && length) {
      const lengthM = parseFloat(length) || 0;
      const widthMm = parseFloat(width) || 0;
      const widthM = widthMm / 1000; // Convert mm to m
      
      // Calculate area in m²
      const area = lengthM * widthM;
      
      // Calculate suggested mixes: area / 3, rounded up (only if width is provided)
      const calcMixes = widthMm > 0 ? Math.ceil(area / 3) : 0;
      
      // Calculate suggested rigid trim: length / 2.5, rounded up (each trim is 2.5m)
      const calcRigidTrim = Math.ceil(lengthM / 2.5);
      
      // Update suggested values
      setSuggestedMixes(calcMixes);
      setSuggestedRigidTrim(calcRigidTrim);
      
      // Only auto-fill the actual values if they haven't been manually set yet
      // or if the user hasn't navigated away and back
      const currentMixes = borderMixesRef.current;
      const currentTrim = rigidTrimRef.current;
      const savedSuggestedMixes = borderRef.current?.suggested_mixes || 0;
      const savedSuggestedTrim = borderRef.current?.suggested_rigid_trim || 0;
      if (currentMixes === 0 || currentMixes === savedSuggestedMixes) {
        setBorderMixes(calcMixes);
      }
      if (currentTrim === 0 || currentTrim === savedSuggestedTrim) {
        setRigidTrim(calcRigidTrim);
      }
    }
  }, [mode, length, width]);

  // Sync to store whenever values change
  useEffect(() => {
    if (showBorder) {
      setBorder({
        mode,
        blend_color: blendColor,
        mixes: borderMixes,
        rigid_trim: rigidTrim,
        formable_trim: formableTrim,
        suggested_mixes: suggestedMixes,
        suggested_rigid_trim: suggestedRigidTrim,
        length: mode === 'calculate' ? parseFloat(length) || 0 : null,
        width: mode === 'calculate' ? parseFloat(width) || 0 : null
      });
    } else {
      setBorder(null);
    }
  }, [showBorder, blendColor, borderMixes, rigidTrim, formableTrim, suggestedMixes, suggestedRigidTrim, mode, length, width, setBorder]);

  const handleAddBorder = () => {
    setShowBorder(true);
  };

  const handleRemoveBorder = () => {
    setShowBorder(false);
    setBorder(null);
    // Reset all values
    setMode('manual');
    setLength('');
    setWidth('');
    setBlendColor('');
    setBorderMixes(0);
    setRigidTrim(0);
    setFormableTrim(0);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'manual') {
      // Clear calculated values when switching to manual
      setLength('');
      setWidth('');
    }
  };

  if (!showBorder) {
    return (
      <div className="mt-6">
        <Button
          onClick={handleAddBorder}
          variant="outline"
          className="w-full h-14 text-lg font-medium rounded-xl border-2 border-dashed border-[#300122] text-[#300122] hover:bg-[#300122]/5"
          data-testid="add-border-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add a Resin Border
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-6 border-2 border-[#300122] rounded-xl p-6 bg-[#300122]/5" data-testid="border-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#27292E]">Resin Border</h3>
        <button
          onClick={handleRemoveBorder}
          className="text-red-500 hover:text-red-700 transition-colors"
          data-testid="remove-border-button"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Mode Selection */}
      <div className="flex gap-2 mb-6">
        <Button
          onClick={() => handleModeChange('manual')}
          variant={mode === 'manual' ? 'default' : 'outline'}
          className={`flex-1 h-10 text-sm font-medium rounded-xl ${
            mode === 'manual' 
              ? 'bg-[#300122] text-white hover:bg-[#300122]/90' 
              : 'border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5'
          }`}
          data-testid="manual-mode-button"
        >
          <Edit3 className="w-3 h-3 mr-1" />
          Manual
        </Button>
        <Button
          onClick={() => handleModeChange('calculate')}
          variant={mode === 'calculate' ? 'default' : 'outline'}
          className={`flex-1 h-10 text-sm font-medium rounded-xl ${
            mode === 'calculate' 
              ? 'bg-[#300122] text-white hover:bg-[#300122]/90' 
              : 'border-2 border-[#300122] text-[#300122] hover:bg-[#300122]/5'
          }`}
          data-testid="calculate-mode-button"
        >
          <Calculator className="w-3 h-3 mr-1" />
          Calculate
        </Button>
      </div>

      {/* Calculate Mode - Length & Width Inputs */}
      {mode === 'calculate' && (
        <BorderCalculateInputs
          length={length}
          setLength={setLength}
          width={width}
          setWidth={setWidth}
          suggestedMixes={suggestedMixes}
          suggestedRigidTrim={suggestedRigidTrim}
        />
      )}

      {/* Blend Color Selection */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="border-blend" className="text-sm font-medium text-gray-700">
            Border Blend Colour *
          </Label>
          <Dialog>
            <DialogTrigger asChild>
              <button
                className="flex items-center gap-1 text-xs text-[#2F5E96] hover:text-[#254b7a] font-medium transition-colors"
                type="button"
              >
                <Eye className="w-4 h-4" />
                View Blends
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#2F5E96]">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white">Surface Blends</DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_resin-quote-calc/artifacts/h3qj1313_b28c5920-5008-446f-a2d9-09ec4fb00671.jpeg"
                  alt="Surface Blends Brochure"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <Select
          value={blendColor}
          onValueChange={setBlendColor}
        >
          <SelectTrigger
            id="border-blend"
            className="h-14 text-lg border-2 focus:border-[#300122] rounded-xl"
            data-testid="border-blend-select"
          >
            <SelectValue placeholder="Select a blend colour" />
          </SelectTrigger>
          <SelectContent>
            {SURFACE_BLENDS.map((group) => (
              <SelectGroup key={group.group}>
                <SelectLabel className="text-sm font-bold text-[#300122] bg-[#300122]/10">
                  {group.group}
                </SelectLabel>
                {group.blends.map((blend) => (
                  <SelectItem key={blend} value={blend}>
                    {blend}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Border Mixes */}
      <BorderStepperInput
        label="Border Mixes"
        labelHint={mode === 'calculate' && length && width ? '(calculated)' : ''}
        value={borderMixes}
        setValue={setBorderMixes}
        testidPrefix="border-mixes"
        unit="mixes"
      />

      {/* 18mm Rigid Trim */}
      <BorderStepperInput
        label="18mm Rigid Trim"
        labelHint={mode === 'calculate' && length ? '(calculated)' : ''}
        value={rigidTrim}
        setValue={setRigidTrim}
        testidPrefix="rigid-trim"
        unit="lengths"
        showLengthFooter
      />

      {/* 18mm Formable Trim */}
      <BorderStepperInput
        label="18mm Formable Trim"
        value={formableTrim}
        setValue={setFormableTrim}
        testidPrefix="formable-trim"
        unit="lengths"
        showLengthFooter
        className="mb-2"
      />
    </div>
  );
};

export default Border;
